import { useEffect, useState } from 'react';
import { useLocale } from '../../lib/locale';
import {
  X,
  CreditCard,
  Smartphone,
  Wallet,
  Banknote,
  CheckCircle2,
  ArrowLeft,
  Lock,
  Loader2,
  Building2,
} from 'lucide-react';

// Payment Logo Components
function GPayLogo({ size = 'h-6' }: { size?: string }) {
  return (
    <div className={`${size} aspect-[3/1] bg-white rounded px-2 flex items-center justify-center border border-gray-200 shadow-sm`}>
      <div className="flex items-center gap-0.5">
        <span className="text-[10px] font-bold text-blue-600">G</span>
        <span className="text-[10px] font-semibold text-gray-700">Pay</span>
      </div>
    </div>
  );
}

function PhonePeLogo({ size = 'h-6' }: { size?: string }) {
  return (
    <div className={`${size} aspect-[3/1] bg-purple-600 rounded px-2 flex items-center justify-center shadow-sm`}>
      <span className="text-[9px] font-bold text-white">PhonePe</span>
    </div>
  );
}

function PaytmLogo({ size = 'h-8' }: { size?: string }) {
  return (
    <div className={`${size} aspect-square bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center shadow-md`}>
      <span className="text-white font-bold text-lg">₹</span>
    </div>
  );
}

function VisaLogo({ size = 'h-5' }: { size?: string }) {
  return (
    <div className={`${size} aspect-[3/1] bg-blue-700 rounded px-2 flex items-center justify-center`}>
      <span className="text-[10px] font-bold text-white italic">VISA</span>
    </div>
  );
}

function MastercardLogo({ size = 'h-5' }: { size?: string }) {
  return (
    <div className={`${size} aspect-square flex items-center justify-center`}>
      <div className="flex items-center gap-[-4px]">
        <div className="w-3 h-3 bg-red-500 rounded-full opacity-90"></div>
        <div className="w-3 h-3 bg-orange-400 rounded-full opacity-90 -ml-1.5"></div>
      </div>
    </div>
  );
}

function RupayLogo({ size = 'h-5' }: { size?: string }) {
  return (
    <div className={`${size} aspect-[3/1] bg-gradient-to-r from-green-600 to-orange-500 rounded px-2 flex items-center justify-center`}>
      <span className="text-[9px] font-bold text-white">RuPay</span>
    </div>
  );
}

interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  totalAmount: number;
  onPaymentComplete: (paymentMethod: string, paymentDetails?: any) => void;
  cardStorageKey?: string;
  walletStorageKey?: string;
}

type CardBrand = 'Visa' | 'Mastercard' | 'RuPay' | 'Amex' | 'Discover' | 'Card';

interface SavedCard {
  id: string;
  brand: CardBrand;
  last4: string;
  expiry: string;
  isDefault: boolean;
  holderName?: string;
  nickname?: string;
  billingAddress?: string;
  token: string;
  createdAt: string;
}

type WalletProviderId = 'phonepe' | 'gpay' | 'paytm';

interface WalletLinkState {
  linked: boolean;
  mobile?: string;
  linkedAt?: string;
  isDefault?: boolean;
  balance?: number | null;
  transactions?: Array<{ id: string; orderId: string; amount: number; provider: WalletProviderId; createdAt: string }>;
  offers?: string[];
  deviceVerified?: boolean;
  pinRequired?: boolean;
  autoPay?: boolean;
}

type PaymentMethod = 'card' | 'upi' | 'wallet' | 'cash' | 'netbanking';
type PaymentStep = 'select' | 'details' | 'auth' | 'processing' | 'success';

export function PaymentMethodModal({
  isOpen,
  onClose,
  totalAmount,
  onPaymentComplete,
  cardStorageKey,
  walletStorageKey,
}: PaymentMethodModalProps) {
  const { formatMoney } = useLocale();
  const storageKey = cardStorageKey || 'customer_cards_guest';
  const walletKey = walletStorageKey || 'customer_payment_links_guest';
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [step, setStep] = useState<PaymentStep>('select');
  
  // Card details
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [savedCardCvv, setSavedCardCvv] = useState('');
  const [cardNickname, setCardNickname] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [saveCard, setSaveCard] = useState(true);
  const [cardBrand, setCardBrand] = useState<CardBrand>('Card');
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  const [otp, setOtp] = useState('');
  const [cardAdded, setCardAdded] = useState(false);
  const [pendingCardDetails, setPendingCardDetails] = useState<any>(null);
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [selectedSavedCardId, setSelectedSavedCardId] = useState<string | null>(null);
  const [walletLinks, setWalletLinks] = useState<Record<WalletProviderId, WalletLinkState>>(
    {} as Record<WalletProviderId, WalletLinkState>
  );
  
  // UPI details
  const [upiId, setUpiId] = useState('');

  // Net Banking
  const [selectedBank, setSelectedBank] = useState('');
  
  // Wallet
  const [selectedWallet, setSelectedWallet] = useState<'paytm' | 'phonepe' | 'gpay' | null>(null);

  const detectCardBrand = (number: string): CardBrand => {
    const clean = String(number || '').replace(/\D/g, '');
    if (/^4/.test(clean)) return 'Visa';
    if (/^(5[1-5]|2[2-7])/.test(clean)) return 'Mastercard';
    if (/^3[47]/.test(clean)) return 'Amex';
    if (/^6/.test(clean)) return 'RuPay';
    if (/^6011|^65|^64[4-9]/.test(clean)) return 'Discover';
    return 'Card';
  };

  const formatCardNumber = (value: string) => {
    const cleaned = String(value || '').replace(/\D/g, '').slice(0, 19);
    const parts = cleaned.match(/.{1,4}/g);
    return parts ? parts.join(' ') : cleaned;
  };

  const luhnCheck = (value: string) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return false;
    let sum = 0;
    let shouldDouble = false;
    for (let i = digits.length - 1; i >= 0; i -= 1) {
      let digit = Number(digits[i]);
      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
  };

  const isExpiryValid = (value: string) => {
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(value)) return false;
    const [monthStr, yearStr] = value.split('/');
    const month = Number(monthStr);
    const year = Number(yearStr);
    if (!month || Number.isNaN(year)) return false;
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear() % 100;
    if (year < currentYear) return false;
    if (year === currentYear && month < currentMonth) return false;
    return true;
  };

  const validateCardNumber = (value: string) => {
    const clean = String(value || '').replace(/\D/g, '');
    if (!clean) return '';
    const brand = detectCardBrand(clean);
    const isLengthValid =
      (brand === 'Amex' && clean.length === 15) ||
      (brand !== 'Amex' && clean.length === 16);
    if (!/^\d+$/.test(clean) || !isLengthValid || !luhnCheck(clean)) {
      return 'Invalid Card Number';
    }
    return '';
  };

  const validateExpiry = (value: string) => {
    if (!value) return '';
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(value)) return 'Expiry must be in MM/YY format.';
    if (!isExpiryValid(value)) return 'Card Expired';
    return '';
  };

  const validateCvv = (value: string, brand: CardBrand) => {
    if (!value) return '';
    if (!(brand === 'Amex' ? /^\d{4}$/ : /^\d{3}$/).test(value)) return 'Invalid CVV';
    return '';
  };

  const maskCardNumber = (last4: string) => `**** **** **** ${last4}`;

  const tokenizeCard = () => `tok_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  const normalizeStoredCards = (parsed: any[]): SavedCard[] => {
    return (Array.isArray(parsed) ? parsed : []).map((card: any) => {
      const rawBrand = String(card?.brand || card?.type || '').toLowerCase();
      const brand = rawBrand.includes('visa')
        ? 'Visa'
        : rawBrand.includes('master')
        ? 'Mastercard'
        : rawBrand.includes('rupay') || rawBrand.includes('ru-pay')
        ? 'RuPay'
        : rawBrand.includes('amex')
        ? 'Amex'
        : rawBrand.includes('discover')
        ? 'Discover'
        : detectCardBrand(card?.number || '');
      return {
        id: String(card?.id || `card-${Date.now()}`),
        brand,
        last4: String(card?.last4 || '').slice(-4),
        expiry: String(card?.expiry || ''),
        isDefault: Boolean(card?.isDefault),
        holderName: card?.holderName || card?.name,
        nickname: card?.nickname || card?.name || '',
        billingAddress: card?.billingAddress || '',
        token: String(card?.token || tokenizeCard()),
        createdAt: String(card?.createdAt || new Date().toISOString()),
      } as SavedCard;
    });
  };

  const WALLET_PROVIDERS: Array<{ id: WalletProviderId; name: string }> = [
    { id: 'phonepe', name: 'PhonePe' },
    { id: 'gpay', name: 'Google Pay' },
    { id: 'paytm', name: 'Paytm' },
  ];

  const normalizeWalletLinks = (parsed: any) => {
    return WALLET_PROVIDERS.reduce((acc, wallet) => {
      const raw = parsed?.[wallet.id] || {};
      acc[wallet.id] = {
        linked: Boolean(raw?.linked),
        mobile: raw?.mobile || raw?.value || '',
        linkedAt: raw?.linkedAt || '',
        isDefault: Boolean(raw?.isDefault),
        balance: typeof raw?.balance === 'number' ? raw.balance : null,
        transactions: Array.isArray(raw?.transactions) ? raw.transactions : [],
        offers: Array.isArray(raw?.offers) ? raw.offers : [],
        deviceVerified: raw?.deviceVerified !== false,
        pinRequired: Boolean(raw?.pinRequired),
        autoPay: Boolean(raw?.autoPay),
      } as WalletLinkState;
      return acc;
    }, {} as Record<WalletProviderId, WalletLinkState>);
  };

  const updateSavedCards = (next: SavedCard[]) => {
    setSavedCards(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const updateWalletLinks = (next: Record<WalletProviderId, WalletLinkState>) => {
    setWalletLinks(next);
    localStorage.setItem(walletKey, JSON.stringify(next));
  };

  const renderBrandLogo = (brand: CardBrand, size = 'h-5') => {
    switch (brand) {
      case 'Visa':
        return <VisaLogo size={size} />;
      case 'Mastercard':
        return <MastercardLogo size={size} />;
      case 'RuPay':
        return <RupayLogo size={size} />;
      case 'Amex':
        return (
          <div className={`${size} aspect-[3/1] bg-teal-600 rounded px-2 flex items-center justify-center`}>
            <span className="text-[9px] font-bold text-white">AMEX</span>
          </div>
        );
      case 'Discover':
        return (
          <div className={`${size} aspect-[3/1] bg-yellow-500 rounded px-2 flex items-center justify-center`}>
            <span className="text-[8px] font-bold text-white">DISC</span>
          </div>
        );
      default:
        return (
          <div className={`${size} aspect-[3/1] bg-gray-200 rounded px-2 flex items-center justify-center`}>
            <span className="text-[9px] font-semibold text-gray-600">CARD</span>
          </div>
        );
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    try {
      const stored = localStorage.getItem(storageKey);
      const parsed = stored ? JSON.parse(stored) : [];
      const normalized = normalizeStoredCards(parsed);
      setSavedCards(normalized);
      const defaultCard = normalized.find((card) => card.isDefault);
      setSelectedSavedCardId(defaultCard?.id || null);
      const storedWallets = localStorage.getItem(walletKey);
      const parsedWallets = storedWallets ? JSON.parse(storedWallets) : {};
      const normalizedWallets = normalizeWalletLinks(parsedWallets);
      setWalletLinks(normalizedWallets);
      const defaultWallet = WALLET_PROVIDERS.find(
        (wallet) => normalizedWallets[wallet.id]?.linked && normalizedWallets[wallet.id]?.isDefault
      );
      if (defaultWallet) {
        setSelectedMethod('wallet');
        setSelectedWallet(defaultWallet.id);
        setStep('details');
      }
    } catch {
      setSavedCards([]);
      setSelectedSavedCardId(null);
      setWalletLinks({} as Record<WalletProviderId, WalletLinkState>);
    }
  }, [isOpen, storageKey, walletKey]);

  if (!isOpen) return null;

  const resetForm = () => {
    setSelectedMethod(null);
    setStep('select');
    setCardNumber('');
    setCardName('');
    setExpiryDate('');
    setCvv('');
    setSavedCardCvv('');
    setCardNickname('');
    setBillingAddress('');
    setSaveCard(true);
    setCardBrand('Card');
    setCardErrors({});
    setOtp('');
    setCardAdded(false);
    setPendingCardDetails(null);
    setSelectedSavedCardId(null);
    setUpiId('');
    setSelectedBank('');
    setSelectedWallet(null);
  };

  const handleMethodSelect = (method: PaymentMethod) => {
    setSelectedMethod(method);
    setCardErrors({});
    setOtp('');
    if (method === 'cash') {
      // Cash on Delivery - no additional details needed
      handlePayment(method);
    } else {
      if (method === 'wallet') {
        const defaultWallet = WALLET_PROVIDERS.find(
          (wallet) => walletLinks[wallet.id]?.linked && walletLinks[wallet.id]?.isDefault
        );
        if (defaultWallet) {
          setSelectedWallet(defaultWallet.id);
        }
      }
      setStep('details');
    }
  };

  const handlePayment = async (method: PaymentMethod, details?: any) => {
    setStep('processing');

    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));

    if (method === 'card' && details?.saveCard) {
      const newCard: SavedCard = {
        id: `card-${Date.now()}`,
        brand: details.brand,
        last4: details.last4,
        expiry: details.expiryDate,
        isDefault: Boolean(details.isDefault) || savedCards.length === 0,
        holderName: details.cardName,
        nickname: details.nickname,
        billingAddress: details.billingAddress,
        token: tokenizeCard(),
        createdAt: new Date().toISOString(),
      };
      const next = newCard.isDefault ? savedCards.map((card) => ({ ...card, isDefault: false })) : savedCards;
      updateSavedCards([newCard, ...next]);
      setCardAdded(true);
    }

    setStep('success');

    // Wait for success animation
    setTimeout(() => {
      onPaymentComplete(method, details);
      resetForm();
    }, 2000);
  };

  const handleCardPayment = () => {
    const cleanCardNumber = cardNumber.replace(/\s/g, '');
    const brand = detectCardBrand(cleanCardNumber);
    const numberError = validateCardNumber(cleanCardNumber);
    const expiryError = validateExpiry(expiryDate);
    const cvvError = validateCvv(cvv, brand);
    const nameError = cardName.trim() ? '' : 'Cardholder name is required.';
    const errors: Record<string, string> = {};
    if (numberError) errors.number = numberError;
    if (expiryError) errors.expiry = expiryError;
    if (cvvError) errors.cvv = cvvError;
    if (nameError) errors.name = nameError;
    setCardErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setPendingCardDetails({
      brand,
      last4: cleanCardNumber.slice(-4),
      cardName,
      expiryDate,
      cardNumber: maskCardNumber(cleanCardNumber.slice(-4)),
      nickname: cardNickname.trim() || `${brand} Card`,
      billingAddress,
      saveCard,
      isDefault: saveCard && savedCards.length === 0,
    });
    setOtp('');
    setStep('auth');
  };

  const handleSavedCardPayment = () => {
    const selected = savedCards.find((card) => card.id === selectedSavedCardId);
    if (!selected) {
      setCardErrors((prev) => ({ ...prev, saved: 'Please select a saved card.' }));
      return;
    }
    const brand = selected.brand;
    const cvvError = validateCvv(savedCardCvv, brand);
    if (cvvError) {
      setCardErrors((prev) => ({ ...prev, savedCvv: cvvError }));
      return;
    }
    setPendingCardDetails({
      brand: selected.brand,
      last4: selected.last4,
      cardName: selected.holderName || '',
      expiryDate: selected.expiry,
      cardNumber: maskCardNumber(selected.last4),
      nickname: selected.nickname,
      billingAddress: selected.billingAddress,
      token: selected.token,
      saveCard: false,
      isDefault: selected.isDefault,
      savedCard: true,
    });
    setOtp('');
    setStep('auth');
  };

  const handleOtpVerify = () => {
    if (!/^\d{6}$/.test(otp)) {
      setCardErrors((prev) => ({ ...prev, otp: 'Enter the 6-digit OTP' }));
      return;
    }
    setCardErrors((prev) => ({ ...prev, otp: '' }));
    handlePayment('card', pendingCardDetails);
  };

  const handleUpiPayment = () => {
    if (!upiId) {
      alert('Please enter your UPI ID');
      return;
    }

    // Validate UPI ID format
    if (!upiId.includes('@')) {
      alert('Please enter a valid UPI ID (e.g., yourname@paytm)');
      return;
    }

    handlePayment('upi', { upiId });
  };

  const handleNetBankingPayment = () => {
    if (!selectedBank) {
      alert('Please select a bank');
      return;
    }

    handlePayment('netbanking', { bank: selectedBank });
  };

  const handleWalletPayment = () => {
    if (!selectedWallet) {
      alert('Please select a wallet');
      return;
    }
    const walletState = walletLinks[selectedWallet as WalletProviderId];
    if (!walletState?.linked) {
      alert('Please link this wallet in Settings before paying.');
      return;
    }
    if (walletState.deviceVerified === false) {
      alert('Device verification is required for this wallet.');
      return;
    }
    if (walletState.pinRequired) {
      const pin = window.prompt('Enter your payment PIN');
      if (!pin || !/^[0-9]{4}$/.test(pin)) {
        alert('Invalid payment PIN.');
        return;
      }
    }
    const otp = window.prompt('Enter the 6-digit OTP sent by your wallet provider');
    if (!otp || !/^[0-9]{6}$/.test(otp)) {
      alert('Invalid OTP.');
      return;
    }

    const orderId = `ORD${Date.now().toString().slice(-6)}`;
    updateWalletLinks({
      ...walletLinks,
      [selectedWallet as WalletProviderId]: {
        ...walletState,
        balance: typeof walletState.balance === 'number' ? Math.max(walletState.balance - totalAmount, 0) : walletState.balance,
        transactions: [
          {
            id: `wallet-${Date.now()}`,
            orderId,
            amount: totalAmount,
            provider: selectedWallet as WalletProviderId,
            createdAt: new Date().toISOString(),
          },
          ...(walletState.transactions || []),
        ],
      },
    });

    handlePayment('wallet', { walletProvider: selectedWallet, orderId });
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    const brand = detectCardBrand(formatted);
    setCardBrand(brand);
    setCardNumber(formatted);
    const error = validateCardNumber(formatted);
    setCardErrors((prev) => ({ ...prev, number: error }));
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 2) {
      value = value.slice(0, 2) + '/' + value.slice(2, 4);
    }
    if (value.length <= 5) {
      setExpiryDate(value);
      const error = validateExpiry(value);
      setCardErrors((prev) => ({ ...prev, expiry: error }));
    }
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setCvv(value);
    const error = validateCvv(value, cardBrand);
    setCardErrors((prev) => ({ ...prev, cvv: error }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            {step !== 'select' && step !== 'processing' && step !== 'success' && (
              <button
                onClick={() => setStep('select')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {step === 'select' && 'Select Payment Method'}
                {step === 'details' && 'Payment Details'}
                {step === 'auth' && '3D Secure Verification'}
                {step === 'processing' && 'Processing Payment'}
                {step === 'success' && 'Payment Successful!'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">Amount to pay: {formatMoney(totalAmount)}</p>
            </div>
          </div>
          {step === 'select' && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Select Payment Method */}
          {step === 'select' && (
            <div className="space-y-3">
              {/* UPI */}
              <button
                onClick={() => handleMethodSelect('upi')}
                className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">UPI</h3>
                    <p className="text-sm text-gray-600">Pay using UPI ID</p>
                  </div>
                  <div className="flex gap-2">
                    <GPayLogo />
                    <PhonePeLogo />
                  </div>
                </div>
              </button>

              {/* Cards */}
              <button
                onClick={() => handleMethodSelect('card')}
                className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">Credit / Debit Card</h3>
                    <div className="flex gap-1 mt-1">
                      <VisaLogo />
                      <MastercardLogo />
                      <RupayLogo />
                    </div>
                  </div>
                  <Lock className="w-5 h-5 text-gray-400" />
                </div>
              </button>

              {/* Wallets */}
              <button
                onClick={() => handleMethodSelect('wallet')}
                className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Wallet className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">Wallets</h3>
                    <p className="text-sm text-gray-600">Paytm, PhonePe, Google Pay</p>
                  </div>
                </div>
              </button>

              {/* Net Banking */}
              <button
                onClick={() => handleMethodSelect('netbanking')}
                className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-slate-500 to-slate-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">Net Banking</h3>
                    <p className="text-sm text-gray-600">Pay via your bank</p>
                  </div>
                </div>
              </button>

              {/* Cash on Delivery */}
              <button
                onClick={() => handleMethodSelect('cash')}
                className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Banknote className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">Cash on Delivery</h3>
                    <p className="text-sm text-gray-600">Pay when you receive</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Card Details */}
          {step === 'details' && selectedMethod === 'card' && (
            <div className="space-y-5">
              {savedCards.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">Saved Cards</p>
                  {savedCards.map((card) => (
                    <div
                      key={card.id}
                      onClick={() => {
                        setSelectedSavedCardId(card.id);
                        setSavedCardCvv('');
                        setCardErrors({});
                      }}
                      className={`w-full p-4 border-2 rounded-xl transition-all cursor-pointer ${
                        selectedSavedCardId === card.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {renderBrandLogo(card.brand, 'h-6')}
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">
                            {card.nickname || `${card.brand} Card`}
                          </p>
                          <p className="text-sm text-gray-600">
                            {maskCardNumber(card.last4)} - Expires {card.expiry || '--/--'}
                          </p>
                        </div>
                        {card.isDefault && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setSelectedSavedCardId(null);
                      setSavedCardCvv('');
                    }}
                    className="text-sm text-blue-600 font-semibold hover:text-blue-700"
                  >
                    Use a new card
                  </button>
                  {cardErrors.saved && <p className="text-xs text-red-600">{cardErrors.saved}</p>}
                </div>
              )}

              {selectedSavedCardId ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                    <input
                      type="password"
                      value={savedCardCvv}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setSavedCardCvv(value);
                        const selected = savedCards.find((card) => card.id === selectedSavedCardId);
                        if (selected) {
                          const error = validateCvv(value, selected.brand);
                          setCardErrors((prev) => ({ ...prev, savedCvv: error }));
                        }
                      }}
                      placeholder="***"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                        cardErrors.savedCvv ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'
                      }`}
                    />
                    {cardErrors.savedCvv && <p className="text-xs text-red-600 mt-1">{cardErrors.savedCvv}</p>}
                  </div>
                  <button
                    onClick={handleSavedCardPayment}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium mt-2 flex items-center justify-center gap-2"
                  >
                    <Lock className="w-4 h-4" />
                    Pay {formatMoney(totalAmount)}
                  </button>
                  <p className="text-xs text-gray-500 text-center">3D Secure authentication will be used for this payment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={cardNumber}
                        onChange={handleCardNumberChange}
                        placeholder="1234 5678 9012 3456"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                          cardErrors.number ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'
                        }`}
                      />
                      {renderBrandLogo(cardBrand, 'h-6')}
                    </div>
                    {cardErrors.number && <p className="text-xs text-red-600 mt-1">{cardErrors.number}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cardholder Name</label>
                    <input
                      type="text"
                      value={cardName}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setCardName(value);
                        setCardErrors((prev) => ({ ...prev, name: value.trim() ? '' : 'Cardholder name is required.' }));
                      }}
                      placeholder="JOHN DOE"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                        cardErrors.name ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'
                      }`}
                    />
                    {cardErrors.name && <p className="text-xs text-red-600 mt-1">{cardErrors.name}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                      <input
                        type="text"
                        value={expiryDate}
                        onChange={handleExpiryChange}
                        placeholder="MM/YY"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                          cardErrors.expiry ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'
                        }`}
                      />
                      {cardErrors.expiry && <p className="text-xs text-red-600 mt-1">{cardErrors.expiry}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                      <input
                        type="password"
                        value={cvv}
                        onChange={handleCvvChange}
                        placeholder={cardBrand === 'Amex' ? '1234' : '123'}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                          cardErrors.cvv ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'
                        }`}
                      />
                      {cardErrors.cvv && <p className="text-xs text-red-600 mt-1">{cardErrors.cvv}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Card Nickname</label>
                    <input
                      type="text"
                      value={cardNickname}
                      onChange={(e) => setCardNickname(e.target.value)}
                      placeholder="Personal Card"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Billing Address (Optional)</label>
                    <textarea
                      rows={2}
                      value={billingAddress}
                      onChange={(e) => setBillingAddress(e.target.value)}
                      placeholder="Street, City, State, ZIP"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={saveCard}
                        onChange={(e) => setSaveCard(e.target.checked)}
                        className="h-4 w-4 text-blue-600"
                      />
                      Save this card for future payments
                    </label>
                  </div>
                  <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-xs text-blue-700">
                    <span>Card details are encrypted and tokenized for PCI-DSS compliance.</span>
                    <button
                      onClick={() => alert('Card scanning is a premium feature and requires camera access.')}
                      className="text-blue-700 font-semibold hover:text-blue-800"
                    >
                      Scan Card
                    </button>
                  </div>
                  <button
                    onClick={handleCardPayment}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium mt-4 flex items-center justify-center gap-2"
                  >
                    <Lock className="w-4 h-4" />
                    Pay {formatMoney(totalAmount)}
                  </button>
                </div>
              )}
            </div>
          )}
          {/* UPI Details */}
          {step === 'details' && selectedMethod === 'upi' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">UPI ID</label>
                <input
                  type="text"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value.toLowerCase())}
                  placeholder="yourname@paytm"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-2">Enter your UPI ID or mobile number@upi</p>
              </div>
              <button
                onClick={handleUpiPayment}
                className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium mt-6"
              >
                Proceed with UPI
              </button>
            </div>
          )}

          {/* Net Banking Details */}
          {step === 'details' && selectedMethod === 'netbanking' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Bank</label>
                <select
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose your bank</option>
                  <option value="HDFC Bank">HDFC Bank</option>
                  <option value="ICICI Bank">ICICI Bank</option>
                  <option value="State Bank of India">State Bank of India</option>
                  <option value="Axis Bank">Axis Bank</option>
                  <option value="Kotak Mahindra Bank">Kotak Mahindra Bank</option>
                </select>
              </div>
              <button
                onClick={handleNetBankingPayment}
                className="w-full bg-slate-600 text-white py-3 rounded-lg hover:bg-slate-700 transition-colors font-medium mt-2"
              >
                Proceed to Bank
              </button>
            </div>
          )}
          {/* Wallet Selection */}
          {step === 'details' && selectedMethod === 'wallet' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-2">Select your preferred wallet</p>

              {WALLET_PROVIDERS.map((wallet) => {
                const walletState = walletLinks[wallet.id];
                const isLinked = Boolean(walletState?.linked);
                const isSelected = selectedWallet === wallet.id;
                const balance = typeof walletState?.balance === 'number' ? formatMoney(walletState.balance) : null;
                return (
                  <button
                    key={wallet.id}
                    disabled={!isLinked}
                    onClick={() => setSelectedWallet(wallet.id)}
                    className={`w-full p-4 border-2 rounded-xl transition-all text-left ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${!isLinked ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-16">
                        {wallet.id === 'phonepe' ? (
                          <PhonePeLogo size="h-10" />
                        ) : wallet.id === 'gpay' ? (
                          <GPayLogo size="h-10" />
                        ) : (
                          <PaytmLogo size="h-10" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{wallet.name}</p>
                        <p className="text-xs text-gray-500">
                          {isLinked ? (balance ? `Balance: ${balance}` : 'Linked') : 'Not linked'}
                        </p>
                      </div>
                      {isSelected && <CheckCircle2 className="w-5 h-5 text-blue-600 ml-auto" />}
                    </div>
                  </button>
                );
              })}

              <button
                onClick={handleWalletPayment}
                disabled={!selectedWallet}
                className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Pay {formatMoney(totalAmount)}
              </button>
              <p className="text-xs text-gray-500 text-center">
                OTP, device verification, and payment PIN may be required
              </p>
            </div>
          )}
          {/* 3D Secure / OTP */}
          {step === 'auth' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                You are being redirected to your bank for 3D Secure verification. Enter the OTP sent to your mobile.
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">OTP</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(value);
                    setCardErrors((prev) => ({ ...prev, otp: value.length === 6 ? '' : prev.otp }));
                  }}
                  placeholder="123456"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    cardErrors.otp ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {cardErrors.otp && <p className="text-xs text-red-600 mt-1">{cardErrors.otp}</p>}
              </div>
              <button
                onClick={handleOtpVerify}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Verify & Pay
              </button>
              <p className="text-xs text-gray-500 text-center">
                3D Secure helps prevent unauthorized transactions
              </p>
            </div>
          )}
          {/* Processing */}
          {step === 'processing' && (
            <div className="py-12 text-center">
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Processing Payment</h3>
              <p className="text-gray-600">Please wait while we process your payment...</p>
            </div>
          )}

          {/* Success */}
          {step === 'success' && (
            <div className="py-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Payment Successful!</h3>
              <p className="text-gray-600 mb-4">Your order has been placed successfully</p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  Amount Paid: <span className="font-semibold text-green-700">{formatMoney(totalAmount)}</span>
                </p>
              </div>
              {cardAdded && (
                <div className="mt-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                  Card Added Successfully
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}




